import { useState, type ChangeEvent, type FormEvent } from "react";
import { contactEmail, contactPhone, contactAddress, whatsappUrl } from "@/mocks/serra";

const inputClass =
  "w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-orange focus:border-transparent transition-all text-sm";

const FORM_ENDPOINT = `https://formsubmit.co/ajax/${contactEmail}`;

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState<Status>("idle");

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = (formData.get("_honey") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (response.ok) {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="contato" className="py-24 lg:py-32 bg-blue/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-cyan text-xs font-mono-tech tracking-[0.2em] uppercase mb-4 block">
            Fale Conosco
          </span>
          <h2 className="text-white text-3xl lg:text-5xl font-bold mb-4">
            Vamos conversar sobre o seu projeto
          </h2>
          <p className="text-white/55 text-base lg:text-lg">
            Preencha o formulário e nossa equipe entrará em contato o quanto antes.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-5">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="bg-bg/60 border border-white/10 rounded-2xl p-6 hover:border-orange/50 transition-colors cursor-pointer group"
            >
              <i className="ri-whatsapp-line text-2xl text-orange mb-4 block"></i>
              <h4 className="font-semibold text-white mb-1">WhatsApp</h4>
              <p className="text-white/55 text-sm">{contactPhone}</p>
            </a>

            <a
              href={`mailto:${contactEmail}`}
              className="bg-bg/60 border border-white/10 rounded-2xl p-6 hover:border-orange/50 transition-colors cursor-pointer group"
            >
              <i className="ri-mail-line text-2xl text-orange mb-4 block"></i>
              <h4 className="font-semibold text-white mb-1">E-mail</h4>
              <p className="text-white/55 text-sm break-all">{contactEmail}</p>
            </a>

            <div className="bg-bg/60 border border-white/10 rounded-2xl p-6">
              <i className="ri-map-pin-line text-2xl text-orange mb-4 block"></i>
              <h4 className="font-semibold text-white mb-1">Localização</h4>
              <p className="text-white/55 text-sm">{contactAddress}</p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 bg-bg/60 border border-white/10 rounded-2xl p-8 lg:p-10 grid gap-5"
          >
            {/* FormSubmit.co configuration — no account needed, just a one-time
                confirmation click on contato@serraconsultoria.com the first time. */}
            <input type="hidden" name="_subject" value="Novo contato via site — Serra Consultoria" />
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="_captcha" value="false" />
            <div className="hp-field-wrap" aria-hidden="true">
              <label htmlFor="_honey">Não preencha este campo</label>
              <input id="_honey" name="_honey" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm text-white/70 mb-2">
                  Nome <span className="text-orange">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm text-white/70 mb-2">
                  E-mail <span className="text-orange">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm text-white/70 mb-2">
                Telefone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="(21) 99999-9999"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm text-white/70 mb-2">
                Mensagem <span className="text-orange">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={handleChange}
                placeholder="Conte um pouco sobre o seu projeto..."
                className={`${inputClass} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 font-medium rounded-full bg-orange hover:bg-coral text-bg px-8 py-4 text-base w-full transition-colors duration-300 cursor-pointer disabled:opacity-60"
            >
              {status === "loading" ? (
                "Enviando..."
              ) : (
                <>
                  Enviar mensagem
                  <i className="ri-send-plane-line"></i>
                </>
              )}
            </button>

            {status === "success" && (
              <div className="flex items-center gap-2 text-green bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm">
                <i className="ri-checkbox-circle-line text-cyan"></i>
                Mensagem enviada com sucesso! Entraremos em contato em breve.
              </div>
            )}
            {status === "error" && (
              <div className="flex items-center gap-2 text-orange bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm">
                <i className="ri-error-warning-line"></i>
                Não foi possível enviar agora. Tente novamente ou fale pelo WhatsApp.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
